import { Button } from 'react-bootstrap';
import {
  fetchAndSetTechnicalAssistance,
  handleShowCreateTechnicalAssistanceModal,
} from './edoTechnicalAssistance.helpers';
import { useEffect, useState } from 'react';
import edoTechnicalAssistanceSignal from './edoTechnicalAssistance.signal';
import PaginationItems from 'components/global/Pagination';
import { useLocation } from 'react-router-dom';
import BreadCrumbWrapper from 'components/global/BreadCrumbs/BreadCrumbs';
import TechnicalAssistancesTable from './components/TechnicalAssistancesTable';
import NewTechnicalAssistanceModal from './components/NewTechnicalAssistanceModal';
import { checkEdoPermissionsAcrossPrograms } from '../../../../libs/edoPermissions';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import api from '../../../../api/api';
import loaderSignal from '../../../../signals/Loader.signal';

const EdoTechnicalAssistance = () => {
  const { totalPages } =
    edoTechnicalAssistanceSignal.value;
  const location = useLocation();
  const searchParams = Object.fromEntries(new URLSearchParams(location.search));
  const currentPage = searchParams.page ? Number(searchParams.page) : 1;
  const [canView, setCanView] = useState(false);
  const [canInsert, setCanInsert] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check permissions for viewing and inserting technical assistance
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const userData = userAccountSignal.value.userData;
        
        // Wait for userData to be loaded
        if (!userData) {
          setIsChecking(false);
          loaderSignal.reset();
          return;
        }

        // Fetch funding programs for the EDO
        const fundingPrograms = await api.get({
          path: '/fundingPrograms',
          options: {
            where: {
              edoId: userData?.account.edoId,
            },
          },
        });

        const permissions = await checkEdoPermissionsAcrossPrograms(
          fundingPrograms,
          ['canView', 'canInsert', 'canUpdate', 'canDelete']
        );
        setCanView(permissions.canView);
        setCanInsert(permissions.canInsert);
        setCanUpdate(permissions.canUpdate);
        setCanDelete(permissions.canDelete);
      } catch (error) {
        console.error('Error checking technical assistance permissions:', error);
        setCanView(false);
        setCanInsert(false);
        setCanUpdate(false);
        setCanDelete(false);
      } finally {
        setIsChecking(false);
        loaderSignal.reset();
      }
    };

    checkPermissions();
  }, [userAccountSignal.value.userData]);

  useEffect(() => {
    if (canView) {
      fetchAndSetTechnicalAssistance(currentPage);
    }
  }, [location.search, canView, currentPage]);

  if (isChecking) {
    return (
      <BreadCrumbWrapper labels={['Technical Assistance']}>
        <div className="border border-solid border-gray p-24 bg-white shadow-sm">
          <p className="text-center">Loading...</p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  if (!canView) {
    return (
      <BreadCrumbWrapper labels={['Technical Assistance']}>
        <div className="border border-solid border-gray p-24 bg-white shadow-sm">
          <h3 className="text-center">Access Denied</h3>
          <p className="text-center">
            You do not have permission to view technical assistance.
          </p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  return (
    <BreadCrumbWrapper labels={['Technical Assistance']}>
      <NewTechnicalAssistanceModal canInsert={canInsert} canUpdate={canUpdate} canDelete={canDelete} />
      <div className="border border-solid border-gray p-24 bg-white shadow-sm">
        <h3 className="text-center mb-0">Technical Assistance</h3>
        <hr />
        {canInsert && (
          <div className="d-flex flex-row justify-content-end mb-16">
            <Button
              className="rounded"
              onClick={handleShowCreateTechnicalAssistanceModal}
            >
              New Technical Assistance
            </Button>
          </div>
        )}
        <TechnicalAssistancesTable canUpdate={canUpdate} />
        {totalPages > 1 && (
          <PaginationItems
            className="justify-content-center mt-24"
            totalPages={totalPages}
          />
        )}
      </div>
    </BreadCrumbWrapper>
  );
};

export default EdoTechnicalAssistance;
